import React, { useState, useCallback, useMemo } from 'react';
import { Button, Typography, Tag, Space, Tooltip, Card, message, Modal } from 'antd';
import { Plus, Edit, Trash2 } from 'lucide-react';
import styled from '@emotion/styled';
import { SimpleListView, ColumnConfig } from 'components/listViews/simpleListView';
import useProductGroupOpenApi, { ProductGroupDto, ProductGroupType } from 'apiClients/useProductGroupOpenApiClient';
import { ResponseWrapperMany, EntityMetaData } from 'interfaces/apiTypes';
import { SortOrderEnum, FilterGroup, Base } from 'interfaces/baseTypes';
import { formatDate, getProductGroupTypeText, getProductGroupTypeColor } from 'utility/helper';
import { useTranslation } from 'react-i18next';
import Search from 'antd/es/input/Search';
import ProductGroupCreateDialog from './ProductGroupCreateDialog';
import { useQueryClient } from '@tanstack/react-query';
import { useLocaleStore } from 'stores/localeStore';
import { debounce } from 'lodash';
import { useNavigate } from 'react-router-dom';

const StyledCard = styled(Card)`
  .ant-page-header-heading-left {
    display: none;
  }

  .ant-page-header-heading {
    display: none;
  }
`;

const TableHeader = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: space-between;

  .header-buttons {
    display: flex;
    gap: 0.75rem;
  }
`;

const TableTitle = styled(Typography.Title)`
  margin-bottom: 0.75rem !important;
`;

const TextSpan = styled.span`
  overflow: hidden;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 1;
  max-width: 200px;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
`;

// Extend ProductGroupDto to ensure it satisfies Base interface
interface ProductGroupWithBase extends ProductGroupDto, Base {
  id: number;
  createdAt: string;
  modifiedAt: string;
}

interface ProductGroupListViewProps {
  onCreateClick?: () => void;
  onEditClick?: (productGroup: ProductGroupDto) => void;
  onDeleteClick?: (productGroup: ProductGroupDto) => void;
  onRowClick?: (productGroup: ProductGroupDto) => void;
  filterGroup?: FilterGroup;
  selectable?: boolean;
  onSelectionChange?: (selectedItems: ProductGroupDto[]) => void;
}

const ProductGroupListView: React.FC<ProductGroupListViewProps> = ({
  onCreateClick,
  onEditClick,
  onDeleteClick,
  onRowClick,
  filterGroup,
  selectable = true,
  onSelectionChange,
}) => {
  const { t } = useTranslation();
  const { getProductGroupsPaginated, deleteProductGroup } = useProductGroupOpenApi();
  const [totalCount, setTotalCount] = useState(0);
  const [selectedItems, setSelectedItems] = useState<ProductGroupDto[]>([]);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const queryClient = useQueryClient();
  const { activeLanguage } = useLocaleStore();
  const navigate = useNavigate();
  
  // Debounced search to avoid too many API calls
  const debouncedSetSearchTerm = useMemo(
    () => debounce((value: string) => setSearchTerm(value), 300),
    []
  );
  
  // Default handlers
  const handleDefaultCreateClick = () => {
    setShowCreateDialog(true);
  };

  const handleDefaultEditClick = (productGroup: ProductGroupDto) => {
    navigate(`/productGroups/edit/${productGroup.id}`);
  };

  const handleSearch = (value: string) => {
    const trimmedValue = value.trim();
    debouncedSetSearchTerm(trimmedValue);
  };

  const handleDefaultDeleteClick = (productGroup: ProductGroupDto) => {
    Modal.confirm({
      title: 'Ta bort produktgrupp',
      content: `Är du säker på att du vill ta bort produktgruppen "${productGroup.name}"?`,
      okText: 'Ja, ta bort',
      cancelText: 'Avbryt',
      okType: 'danger',
      onOk: async () => {
        try {
          await deleteProductGroup(productGroup.id || 0);
          message.success('Produktgrupp borttagen');
          
          // Invalidate all product-groups queries to refresh the list
          queryClient.invalidateQueries({ 
            predicate: (query) => 
              Array.isArray(query.queryKey) && 
              query.queryKey[0]?.toString().startsWith('product-groups-lang-')
          });
        } catch (error) {
          message.error('Fel vid borttagning av produktgrupp');
        }
      },
    });
  };

  const handleDefaultRowClick = (productGroup: ProductGroupDto) => {
    navigate(`/productGroups/edit/${productGroup.id}`);
  };

  const handleDefaultSelectionChange = (selectedItems: ProductGroupDto[]) => {
    setSelectedItems(selectedItems);
    console.log('Markering ändrad:', selectedItems);
  };

  const fetchProductGroups = useCallback(
    async (
      page: number,
      pageSize: number,
      order: SortOrderEnum,
      orderByField: string,
      filters?: string
    ): Promise<ResponseWrapperMany<ProductGroupWithBase> | undefined> => {
      const result = await getProductGroupsPaginated({
        page: page + 1, // API expects 1-based page numbers
        pageSize,
        languageId: activeLanguage?.id,
        search: searchTerm || undefined, // Only pass search if not empty
        // Add any additional filters based on filterGroup if needed
      });

      if (result) {
        // Transform the data to ensure it satisfies Base interface
        const transformedItems: ProductGroupWithBase[] = (result.data || []).map(item => ({
          ...item,
          id: item.id || 0,
          createdAt: item.createdAt || new Date().toISOString(),
          modifiedAt: item.modifiedAt || new Date().toISOString(),
        }));

        return {
          items: transformedItems,
          totalCount: result.total || 0,
          metaData: {} as EntityMetaData,
          hasNextPage: false,
        };
      }
      return undefined;
    },
    [getProductGroupsPaginated, activeLanguage?.id, searchTerm]
  );

  const productGroupColumns: ColumnConfig<ProductGroupWithBase>[] = [
    {
      header: 'ID',
      accessorKey: 'id',
      cell: ({ row }) => <span>{row.original.id}</span>,
      size: 80,
    },
    {
      header: 'Namn',
      accessorKey: 'name',
      cell: ({ row }) => (
        <Tooltip title={row.original.name}>
          <TextSpan>{row.original.name || '-'}</TextSpan>
        </Tooltip>
      ),
      size: 150,
    },
    {
      header: 'Typ',
      accessorKey: 'type',
      cell: ({ row }) => (
        <Tag color={getProductGroupTypeColor(row.original.type)}>
          {getProductGroupTypeText(row.original.type)}
        </Tag>
      ),
      size: 80,
    },
    {
      header: 'Beskrivning',
      accessorKey: 'description',
      cell: ({ row }) => (
        <Tooltip title={row.original.description}>
          <TextSpan>{row.original.description || '-'}</TextSpan>
        </Tooltip>
      ),
      size: 120,
    },
    {
      header: 'Antal produkter',
      accessorKey: 'productCount',
      cell: ({ row }) => (
        <span>{row.original.productCount || 0}</span>
      ),
      size: 150
    },
    {
      header: 'Status',
      accessorKey: 'isActive',
      cell: ({ row }) => (
        <Tag color={row.original.isActive ? 'green' : 'red'}>
          {row.original.isActive ? 'Aktiv' : 'Inaktiv'}
        </Tag>
      ),
      size: 100
    },
    {
      header: 'Skapad',
      accessorKey: 'createdAt',
      cell: ({ row }) => (
        <span>
          {row.original.createdAt 
            ? formatDate(row.original.createdAt).split('T')[0]
            : '-'
          }
        </span>
      ),
      size: 120
    },
    {
      header: 'Modifierad',
      accessorKey: 'modifiedAt',
      cell: ({ row }) => (
        <span>
          {row.original.modifiedAt 
            ? formatDate(row.original.modifiedAt).split('T')[0]
            : '-'
          }
        </span>
      ),
      size: 120
    },
  ];

  // Always add actions column with default handlers
  productGroupColumns.push({
    header: 'Åtgärder',
    accessorKey: 'actions',
    cell: ({ row }) => (
      <ActionButtons>
        <Button
          type="text"
          size="small"
          icon={<Edit size={16} />}
          onClick={(e) => {
            e.stopPropagation();
            (onEditClick || handleDefaultEditClick)(row.original);
          }}
          title="Redigera"
        />
        <Button
          type="text"
          size="small"
          danger
          icon={<Trash2 size={16} />}
          onClick={(e) => {
            e.stopPropagation();
            (onDeleteClick || handleDefaultDeleteClick)(row.original);
          }}
          title="Ta bort"
        />
      </ActionButtons>
    ),
    size: 120,
  });

  const renderHeader = useCallback(
    () => (
      <TableHeader>
        <div style={{ width: "100%" }}>
          <Search
            placeholder="Sök produktgrupp..."
            onSearch={handleSearch}
            allowClear
            enterButton={true}
          />    
        </div>
        <div className="header-buttons">
        <Button
            style={{
                textAlign: "left",
                display: "flex",
                alignItems: "center",
            }}
            type="primary"
            icon={<Plus size={16} />}
            onClick={onCreateClick || handleDefaultCreateClick}
        >
            Skapa produktgrupp
        </Button>
        </div>
      </TableHeader>
    ),
    [onCreateClick]
  );

  return (
    <StyledCard bordered={false} className="criclebox h-full">
      <TableTitle level={4}>Produktgrupper</TableTitle>
      <SimpleListView<ProductGroupWithBase>
        columns={productGroupColumns}
        fetchItems={fetchProductGroups}
        queryKey={`product-groups-lang-${activeLanguage?.id || 1}-search-${searchTerm}`}
        equalColumnWidths={false}
        resizableColumns={true}
        selectable={selectable}
        onSelectionChange={onSelectionChange || handleDefaultSelectionChange}
        onRowClick={onRowClick || handleDefaultRowClick}
        renderHeader={renderHeader}
        filterGroup={filterGroup}
        onCountChange={setTotalCount}
        defaultSorting={{
          fieldName: 'id',
          sortOrder: SortOrderEnum.ASC,
        }}
      />
      
      <ProductGroupCreateDialog
        isOpen={showCreateDialog}
        onClose={() => setShowCreateDialog(false)}
        onSuccess={() => {
          // The list will refresh automatically due to React Query
          message.success('Produktgruppen har skapats och listan uppdateras');
          // Invalidate all product-groups queries for all languages and search terms
          queryClient.invalidateQueries({ 
            predicate: (query) => 
              Array.isArray(query.queryKey) && 
              query.queryKey[0]?.toString().startsWith('product-groups-lang-')
          });
        }}
      />
    </StyledCard>
  );
};

export default ProductGroupListView;
